import { computed, inject, Injector, type Signal } from '@angular/core';
import type { IconName } from '@lite-workspace/shared/blocks/icon';
import { SupportDeeplinkService } from '@lite-workspace/shared/directives/go-to-support';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { SupportWidgetService } from '@lite-workspace/shared/support-widget';
import { SUPPORT_VK_CHAT_URL } from '@lite-workspace/dto-common';

export interface SupportChannel {
  kind: string;
  label: string;
  icon: IconName;
  /** Выделенный канал: по нему отвечают быстрее всего. */
  accent: boolean;
  open: () => void;
  /** Подготовить переход заранее — зовёт открытие меню или карточки со списком каналов. */
  prepare?: () => void;
  /** Закрывает группу чата на странице: меню ставит разделитель под строкой. */
  separatorAfter?: boolean;
}

/**
 * One look of the channel list, wherever it opens: account-menu submenu and the standalone channel menu.
 * The plate grows with its longest label, a row never wraps.
 */
export const SUPPORT_CHANNEL_MENU = {
  plate: 'min-w-49 gap-0',
  row: 'px-3 py-2.75 text-c2 whitespace-nowrap',
} as const;

export const SUPPORT_CHAT_HERE_LABEL = 'Чат на сайте';

/** Столько ждём, что приложение Telegram заберёт фокус; не забрало — приложения, похоже, нет. */
export const TELEGRAM_OPEN_WAIT_MS = 1500;

const openVkChat = (): void => void window.open(SUPPORT_VK_CHAT_URL, '_blank', 'noopener');

/** Каналы связи: чат на странице, пока виджет поддержки запущен, затем Telegram и ВКонтакте. */
export function supportChannels(): Signal<readonly SupportChannel[]> {
  const injector = inject(Injector);
  const widget = inject(SupportWidgetService);
  // Сервис берётся на клике, а не здесь: хосту без HttpClient (витрина, спеки) список нужен и так.
  const deeplink = (): SupportDeeplinkService => injector.get(SupportDeeplinkService);
  // t.me does not load without a VPN in Russia: the row opens the Telegram app itself. Переход синхронный, на
  // самом жесте: после `await` минта Safari и Firefox внешний протокол уже не запускают. Ссылку минтит заранее
  // `prepare`; не успела — бот без токена.
  const telegram = (): void => {
    window.open(deeplink().takeAppUrl(), '_self');
    hintWhenTelegramStaysClosed(injector.get(ToastService));
  };

  const prepare = (): void => {
    try {
      deeplink().prefetchAppUrl();
    } catch {
      // Хост без HttpClient (витрина, спеки) минтить не может — переход возьмёт бота без токена.
    }
  };

  return computed<readonly SupportChannel[]>(() => [
    // A hidden page (student screens) cannot open the chat, so it offers no row for it.
    ...(widget.running() && widget.zone() !== 'hidden' ? chatGroup(widget) : []),
    {
      kind: 'telegram',
      label: 'Telegram',
      icon: 'telegram',
      accent: true,
      open: telegram,
      prepare,
    },
    {
      kind: 'vk',
      label: 'ВКонтакте',
      icon: 'message-circle',
      accent: false,
      open: () => void window.open(deeplink().takeVkUrl(), '_blank', 'noopener'),
      prepare: () => {
        try {
          deeplink().prefetchVkUrl();
        } catch {
          // Хост без HttpClient (витрина, спеки) минтить не может — откроется беседа без кода.
        }
      },
    },
  ]);
}

function chatGroup(widget: SupportWidgetService): SupportChannel[] {
  return [
    {
      kind: 'chat',
      label: SUPPORT_CHAT_HERE_LABEL,
      icon: 'message-square',
      accent: false,
      open: () => widget.open(),
      separatorAfter: true,
    },
  ];
}

/**
 * Открывшееся приложение Telegram уводит фокус со страницы. Страница его не потеряла за `TELEGRAM_OPEN_WAIT_MS` —
 * Telegram на устройстве нет, и строка поддержки иначе осталась бы мёртвой: зовём во ВКонтакте.
 */
function hintWhenTelegramStaysClosed(toast: ToastService): void {
  let left = false;
  const leave = (): void => {
    left = true;
  };

  const hidden = (): void => {
    if (document.visibilityState === 'hidden') left = true;
  };

  window.addEventListener('blur', leave);
  document.addEventListener('visibilitychange', hidden);
  setTimeout(() => {
    window.removeEventListener('blur', leave);
    document.removeEventListener('visibilitychange', hidden);
    if (!left) toast.action('Telegram не открылся? Напишите во ВКонтакте', 'Написать', openVkChat);
  }, TELEGRAM_OPEN_WAIT_MS);
}
